import { HIHistoryAPI } from '@/entities/monitoring/hi';
import { Duration } from '@/shared/ui/duration';
import { HI_STATUS_COLOR } from '../config/status';
import { useMarqueeDuration } from '@/shared/lib/hooks/use-marquee-duration';
import { HIValueData } from '@/entities/monitoring/hi/lib/parser';
import { interleaveNext } from '@/shared/lib/utils/array-interleave';
import { getHIColourByStatus } from '@/entities/monitoring/hi/lib/status-color';
import { getHINameByValue } from '@/entities/monitoring/hi/lib/status-name';
import { Marquee } from '@gfazioli/mantine-marquee';
import {
  Alert,
  Badge,
  type BadgeProps,
  Divider,
  Flex,
  Group,
  HoverCard,
  Indicator,
  isLightColor,
  LoadingOverlay,
  Notification,
  Paper,
  rgba,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import IconAlertCircle from '@tabler/icons-react/dist/esm/icons/IconAlertCircle.mjs';
import { useQuery } from '@tanstack/react-query';
import cs from 'clsx';
import dayjs from 'dayjs';
import { omit } from 'lodash';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ValueProps {
  data: HIValueData;
  grouped?: boolean;
  groupedClassNames?: {
    container?: string;
    leftBadge?: BadgeProps['classNames'];
    rightBadge?: BadgeProps['classNames'];
  };
  onClick?: () => void;
  'data-testid'?: string;
}
const speedPxPerSec = 60;
const AnnotationsRender = ({
  hi_id,
  color,
}: {
  hi_id: string;
  color: string;
}) => {
  const { t } = useTranslation();
  const [marquee, setMarquee] = useState<HTMLDivElement>(null);
  const duration = useMarqueeDuration(marquee, speedPxPerSec);

  const { data, isLoading } = useQuery({
    queryKey: ['hi-annotations', hi_id],
    queryFn: () => HIHistoryAPI.getHIAnnotations(hi_id).then((res) => res.data),
    cacheTime: 5000,

    placeholderData: { annotations: [] },
  });
  const annotations = data?.annotations || [];

  return isLoading ? (
    <LoadingOverlay visible />
  ) : (
    annotations?.length > 0 && (
      <>
        <Divider
          my={0}
          label={`${t('widgets.charts.hiValue.events', { ns: 'dashboard-tab' })}: ${annotations.length}`}
          labelPosition='left'
        />
        {annotations.length > 1 ? (
          <Marquee
            data-testid='hi-annotations'
            ref={(e) => {
              setMarquee(e);
            }}
            w='100%'
            h={'fit-content'}
            // fadeEdges
            repeat={0}
            duration={duration / 2}
            // fadeEdgesSize='xs'
            // fadeEdgesColor={
            //   colorScheme === 'dark'
            //     ? 'var(--mantine-color-dark-6)'
            //     : 'var(--mantine-color-gray-0)'
            // }
          >
            {annotations
              .toSorted((a, b) => {
                if (b.severity !== a.severity) {
                  return b.severity - a.severity;
                }
                return (
                  dayjs(b.started_at).valueOf() - dayjs(a.started_at).valueOf()
                );
              })
              ?.map((annotation, index) => {
                const annotationMetadata = omit(annotation, [
                  'started_at',
                  'severity',
                ]);
                return (
                  <Indicator
                    key={index}
                    label={index + 1}
                    color='gray'
                    mt='xs'
                    size={14}
                    disabled={annotations?.length < 2}
                  >
                    <Notification
                      withBorder
                      classNames={{
                        root: '!shadow-none',
                        description: '!text-xs',
                      }}
                      styles={{
                        root: {
                          backgroundColor: rgba(color, 0.1),
                        },
                      }}
                      withCloseButton={false}
                      color={HI_STATUS_COLOR[annotation.severity]}
                    >
                      <Stack gap={'xs'}>
                        <Group wrap='nowrap'>
                          <Text size={'xs'} fw={'bold'} className='text-nowrap'>
                            {dayjs(annotation.started_at).format(
                              'DD.MM.YYYY HH:mm:ss',
                            )}
                          </Text>
                        </Group>
                        <Group wrap='nowrap'>
                          {interleaveNext(
                            Object.keys(annotationMetadata).map((key) => (
                              <Stack key={key} gap={0}>
                                <Text
                                  size={'xs'}
                                  lineClamp={1}
                                  fw={'bold'}
                                  className='text-nowrap'
                                >
                                  {key}
                                </Text>
                                <Text
                                  size={'xs'}
                                  lineClamp={1}
                                  className='text-nowrap'
                                >
                                  {annotationMetadata[key]}
                                </Text>
                              </Stack>
                            )),
                            () => (
                              <Divider orientation='vertical' />
                            ),
                          )}
                        </Group>
                      </Stack>
                    </Notification>
                  </Indicator>
                );
              })}
          </Marquee>
        ) : (
          <Indicator
            color='gray'
            mt='xs'
            size={14}
            disabled={annotations?.length < 2}
          >
            <Notification
              withBorder
              classNames={{
                root: '!shadow-none',
                description: '!text-xs',
              }}
              styles={{
                root: {
                  backgroundColor: rgba(color, 0.1),
                },
              }}
              withCloseButton={false}
              color={color}
            >
              <Stack gap={'xs'}>
                <Group wrap='nowrap'>
                  <Text size={'xs'} fw={'bold'} className='text-nowrap'>
                    {dayjs(annotations[0]?.started_at).format(
                      'DD.MM.YYYY HH:mm:ss',
                    )}
                  </Text>
                </Group>
                {interleaveNext(
                  Object.keys(
                    omit(annotations[0], ['started_at', 'severity']),
                  ).map((key) => (
                    <Stack key={key} gap={0}>
                      <Text
                        size='xs'
                        lineClamp={1}
                        fw='bold'
                        tt='capitalize'
                        className='text-nowrap'
                      >
                        {key}
                      </Text>
                      <Text size={'xs'} lineClamp={1} className='text-nowrap'>
                        {annotations[0]?.[key]}
                      </Text>
                    </Stack>
                  )),
                  () => (
                    <Divider orientation='vertical' />
                  ),
                )}
              </Stack>
            </Notification>
          </Indicator>
        )}
      </>
    )
  );
};
export const HIValueCell = memo(
  ({
    data,
    grouped,
    groupedClassNames,
    onClick,
    'data-testid': dataTestId,
  }: ValueProps) => {
    const { t } = useTranslation();
    const color = HI_STATUS_COLOR[data.value];

    return (
      <HoverCard position={'top'} withArrow>
        <HoverCard.Target>
          <UnstyledButton
            p={0}
            h={26}
            onClick={onClick}
            data-testid={dataTestId}
            className='overflow-hidden'
          >
            {grouped ? (
              <Paper bg={color} h={26} className={cs()} px='xs'>
                <Text
                  h={26}
                  fw={700}
                  tt='uppercase'
                  fz={13}
                  truncate='end'
                  style={{
                    lineHeight: '26px',
                    color: isLightColor(color) ? 'black' : 'white',
                  }}
                >
                  {data.label}
                </Text>
              </Paper>
            ) : (
              <Flex
                wrap={'nowrap'}
                align={'center'}
                className={cs(
                  'overflow-hidden !items-center',
                  groupedClassNames?.container,
                )}
              >
                <Badge
                  size={'lg'}
                  color={color}
                  classNames={{
                    ...{
                      root: '!overflow-visible !rounded-r-none pointer-events-none',
                      label: '!overflow-visible',
                    },
                    ...groupedClassNames?.leftBadge,
                  }}
                  variant={'dot'}
                >
                  {data?.ci?.ci_name}
                </Badge>
                <Badge
                  size={'lg'}
                  color={color}
                  classNames={{
                    ...{
                      root: 'w-fit overflow-visible !rounded-l-none pointer-events-none',
                    },
                    ...groupedClassNames?.rightBadge,
                  }}
                  variant='filled'
                  autoContrast
                >
                  {data.label}
                </Badge>
              </Flex>
            )}
          </UnstyledButton>
        </HoverCard.Target>
        <HoverCard.Dropdown bd={rgba(color, 0.1)} p={0}>
          <Stack p='xs' gap={0} className='relative'>
            <Alert
              icon={<IconAlertCircle size={18} />}
              title={t(getHINameByValue(data.value))}
              color={getHIColourByStatus(data.value)}
              mb='xs'
            >
              <Stack gap={0}>
                <Group>
                  <Text size={'xs'}>
                    {t('widgets.charts.hiValue.duration', {
                      ns: 'dashboard-tab',
                    })}
                  </Text>
                  <Text size={'xs'} fw={'bold'}>
                    <Duration date={dayjs(data.started_at)} />
                  </Text>
                </Group>

                <Group>
                  <Text size={'xs'}>
                    {t('widgets.charts.hiValue.startDate', {
                      ns: 'dashboard-tab',
                    })}
                  </Text>
                  <Text size={'xs'} fw={'bold'}>
                    {dayjs(data.started_at).format('DD.MM.YYYY HH:mm:ss')}
                  </Text>
                </Group>
              </Stack>
            </Alert>
            <AnnotationsRender hi_id={data?.hi_id} color={color} />
          </Stack>
        </HoverCard.Dropdown>
      </HoverCard>
    );
  },
);